export type AppErrorKind = 'database' | 'query' | 'network' | 'unknown';

export type AppErrorField = {
    label: string;
    value: string;
};

export type AppErrorDetails = {
    kind: AppErrorKind;
    title: string;
    summary: string;
    name: string;
    message: string;
    fields: AppErrorField[];
    stack?: string;
    query?: string;
    params?: string;
};

const PG_FIELD_KEYS = [
    'code',
    'severity',
    'detail',
    'hint',
    'position',
    'internalPosition',
    'internalQuery',
    'where',
    'schema',
    'table',
    'column',
    'dataType',
    'constraint',
    'file',
    'line',
    'routine',
] as const;

const PG_CODE_LABELS: Record<string, string> = {
    '08000': 'connection exception',
    '08001': 'SQL client unable to establish connection',
    '08003': 'connection does not exist',
    '08006': 'connection failure',
    '08004': 'SQL server rejected establishment of connection',
    '28P01': 'invalid password',
    '28000': 'invalid authorization specification',
    '3D000': 'invalid catalog name',
    '42P01': 'undefined table',
    '42703': 'undefined column',
    '23505': 'unique violation',
    '23503': 'foreign key violation',
    '23502': 'not null violation',
    '22P02': 'invalid text representation',
    '42601': 'syntax error',
    '42701': 'duplicate column',
    '42P07': 'duplicate table',
    '53300': 'too many connections',
    '57P01': 'admin shutdown',
    '57P03': 'cannot connect now',
};

type PlainRecord = Record<string, unknown>;

function isRecord(value: unknown): value is PlainRecord {
    return typeof value === 'object' && value !== null;
}

function asString(value: unknown): string | undefined {
    if (value === null || value === undefined) return undefined;
    if (typeof value === 'string') {
        const trimmed = value.trim();
        return trimmed === '' ? undefined : trimmed;
    }
    if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') {
        return String(value);
    }
    try {
        return JSON.stringify(value, null, 2);
    } catch {
        return String(value);
    }
}

function readProp(error: unknown, key: string): unknown {
    if (!isRecord(error)) return undefined;
    return error[key];
}

function isPgDatabaseError(error: unknown): boolean {
    if (!isRecord(error)) return false;
    if (error.name === 'DatabaseError') return true;
    // pg DatabaseError always carries a 5-char SQLSTATE when from the server
    const code = asString(error.code);
    return Boolean(code && /^[0-9A-Z]{5}$/.test(code) && (error.severity || error.detail || error.schema || error.table));
}

function isDrizzleQueryError(error: unknown): boolean {
    if (!isRecord(error)) return false;
    return typeof error.query === 'string' && Array.isArray(error.params);
}

function isNetworkError(error: unknown): boolean {
    if (error instanceof TypeError) {
        const message = error.message;
        if (message === 'Failed to fetch'
            || message === 'NetworkError when attempting to fetch resource.') {
            return true;
        }
    }
    const code = asString(readProp(error, 'code'));
    if (!code) return false;
    return [
        'ECONNREFUSED',
        'ENOTFOUND',
        'EAI_AGAIN',
        'ETIMEDOUT',
        'ECONNRESET',
        'EPIPE',
        'ENETUNREACH',
        'EHOSTUNREACH',
    ].includes(code);
}

function collectCauseChain(error: unknown, limit = 8): unknown[] {
    const chain: unknown[] = [];
    let current: unknown = error;
    const seen = new Set<unknown>();

    while (current && chain.length < limit && !seen.has(current)) {
        seen.add(current);
        chain.push(current);
        current = isRecord(current) ? current.cause : undefined;
    }

    return chain;
}

function addField(fields: AppErrorField[], label: string, value: unknown) {
    const text = asString(value);
    if (!text) return;
    if (fields.some((field) => field.label === label && field.value === text)) return;
    fields.push({ label, value: text });
}

function extractPgFields(error: unknown, fields: AppErrorField[]) {
    if (!isRecord(error)) return;

    for (const key of PG_FIELD_KEYS) {
        const value = error[key];
        if (value === undefined || value === null || value === '') continue;
        if (key === 'code') {
            const code = asString(value);
            const label = code ? PG_CODE_LABELS[code] : undefined;
            addField(fields, 'SQLSTATE', label ? `${code} (${label})` : code);
            continue;
        }
        const label = key
            .replace(/([A-Z])/g, ' $1')
            .replace(/^./, (c) => c.toUpperCase());
        addField(fields, label, value);
    }
}

function extractNodeFields(error: unknown, fields: AppErrorField[]) {
    if (!isRecord(error)) return;
    for (const key of ['code', 'errno', 'syscall', 'address', 'port', 'hostname', 'path'] as const) {
        addField(fields, key, error[key]);
    }
}

function extractDrizzleFields(error: unknown, fields: AppErrorField[]) {
    if (!isDrizzleQueryError(error) || !isRecord(error)) return;
    addField(fields, 'SQL', error.query);
    if (Array.isArray(error.params) && error.params.length > 0) {
        addField(fields, 'Params', error.params);
    }
}

function inspectSerializedAppError(error: unknown): AppErrorDetails | null {
    if (!isRecord(error) || error.__appErrorSerialized !== true) return null;

    const kind = (asString(error.kind) as AppErrorKind | undefined) ?? 'unknown';
    const fields = Array.isArray(error.fields)
        ? error.fields
            .filter((field): field is AppErrorField => (
                isRecord(field)
                && typeof field.label === 'string'
                && typeof field.value === 'string'
            ))
        : [];

    const summary =
        asString(error.summary)
        ?? asString(error.message)
        ?? (error instanceof Error ? error.message : 'Unknown error');

    return {
        kind,
        title: asString(error.title) ?? (
            kind === 'database'
                ? 'שגיאת מסד נתונים'
                : kind === 'query'
                    ? 'שגיאת שאילתה'
                    : kind === 'network'
                        ? 'שגיאת חיבור'
                        : 'שגיאה'
        ),
        summary,
        name: asString(error.name) ?? (error instanceof Error ? error.name : 'Error'),
        message: asString(error.message) ?? summary,
        fields,
        stack: asString(error.stack) ?? (error instanceof Error ? error.stack : undefined),
        query: asString(error.query),
        params: asString(error.params),
    };
}

/**
 * Build a structured view of any thrown value, with extra detail for
 * PostgreSQL / Drizzle / connection failures.
 */
export function inspectAppError(error: unknown): AppErrorDetails {
    const serialized = inspectSerializedAppError(error);
    if (serialized) return serialized;

    const chain = collectCauseChain(error);
    const root = chain[0];
    const pgError = chain.find(isPgDatabaseError);
    const drizzleError = chain.find(isDrizzleQueryError);
    const networkError = chain.find(isNetworkError);

    const fields: AppErrorField[] = [];
    let kind: AppErrorKind = 'unknown';
    let title = 'שגיאה';

    // Prefer the underlying cause. Drizzle wraps connection/DNS failures as
    // "Failed query: …", which looks like bad SQL even when Postgres is fine.
    if (networkError) {
        kind = 'network';
        title = 'שגיאת חיבור';
        extractNodeFields(networkError, fields);
    } else if (pgError) {
        kind = 'database';
        title = 'שגיאת מסד נתונים';
        extractPgFields(pgError, fields);
    } else if (drizzleError) {
        kind = 'query';
        title = 'שגיאת שאילתה';
    }

    if (drizzleError) {
        extractDrizzleFields(drizzleError, fields);
    }

    if (pgError && kind !== 'database') {
        extractPgFields(pgError, fields);
    }

    for (const item of chain) {
        if (item === pgError || item === drizzleError || item === networkError) continue;
        if (isNetworkError(item)) extractNodeFields(item, fields);
        if (isPgDatabaseError(item)) extractPgFields(item, fields);
    }

    const name =
        asString(readProp(root, 'name'))
        ?? (root instanceof Error ? root.name : 'Error');

    const message =
        asString(readProp(root, 'message'))
        ?? (root instanceof Error ? root.message : String(root ?? 'Unknown error'));

    const networkMessage = networkError ? asString(readProp(networkError, 'message')) : undefined;
    const pgMessage = pgError ? asString(readProp(pgError, 'message')) : undefined;
    const summary = networkMessage || pgMessage || message;

    addField(fields, 'Name', name);
    if (message && message !== summary) {
        addField(fields, 'Message', message);
    }

    for (let i = 1; i < chain.length; i += 1) {
        const cause = chain[i];
        const causeName = asString(readProp(cause, 'name')) ?? 'Error';
        const causeMessage = asString(readProp(cause, 'message')) ?? String(cause);
        addField(fields, `Cause ${i}`, `${causeName}: ${causeMessage}`);
    }

    const stack =
        asString(readProp(pgError ?? drizzleError ?? root, 'stack'))
        ?? (root instanceof Error ? root.stack : undefined);

    const query = drizzleError && isRecord(drizzleError)
        ? asString(drizzleError.query)
        : undefined;
    const params = drizzleError && isRecord(drizzleError) && Array.isArray(drizzleError.params)
        ? asString(drizzleError.params)
        : undefined;

    return {
        kind,
        title,
        summary,
        name,
        message,
        fields,
        stack,
        query,
        params,
    };
}

export function formatAppErrorReport(
    details: AppErrorDetails,
    extras?: {
        componentStack?: string;
        url?: string;
    },
): string {
    const lines: string[] = [];

    if (extras?.url) {
        lines.push(`URL: ${extras.url}`);
    }

    lines.push(
        `Title: ${details.title}`,
        `Kind: ${details.kind}`,
        `Name: ${details.name}`,
        '',
        'Summary:',
        details.summary,
    );

    if (details.fields.length > 0) {
        lines.push('', 'Details:');
        for (const field of details.fields) {
            lines.push(`${field.label}: ${field.value}`);
        }
    }

    if (details.stack) {
        lines.push('', 'Stack:', details.stack);
    }

    if (extras?.componentStack) {
        lines.push('', 'Component stack:', extras.componentStack);
    }

    return lines.join('\n');
}

/**
 * Flatten nested DB/query errors into a single Error whose message and
 * enumerable properties survive TanStack Start serialization.
 */
export function toSerializableAppError(error: unknown): Error {
    if (isRecord(error) && error.__appErrorSerialized === true && error instanceof Error) {
        return error;
    }

    const details = inspectAppError(error);
    const lines = [
        details.summary,
        '',
        `Kind: ${details.kind}`,
        ...details.fields.map((field) => `${field.label}: ${field.value}`),
    ];

    if (details.stack) {
        lines.push('', 'Stack:', details.stack);
    }

    const serialized = new Error(lines.join('\n'));
    serialized.name = details.kind === 'database'
        ? 'DatabaseError'
        : details.kind === 'query'
            ? 'DrizzleQueryError'
            : details.name || 'Error';

    Object.assign(serialized, {
        __appErrorSerialized: true,
        kind: details.kind,
        title: details.title,
        summary: details.summary,
        fields: details.fields,
        query: details.query,
        params: details.params,
    });

    if (details.stack) {
        serialized.stack = details.stack;
    }

    return serialized;
}
